import { useModuleEnabled } from "../access/useFeatureAccess";
import { useTopNavWorklistVisible } from "./useTopNavWorklistVisible";

/**
 * Is the ADT notification bell visible for the active persona?
 *
 * Gated by PERSONA/MODULE RBAC ALONE — deliberately no VITE build-time flag.
 * The `AdtNotifications` `persona_modules` grant (see `roles.ts`'s PROVIDER
 * seed, `featureCatalog.ts`'s catalog entry) IS the on/off switch: it's
 * real server-side RBAC that needs no redeploy, so a build-time flag layered
 * on top would just be redundant ceremony (user directive, amending the
 * original P4 brief).
 *
 * Requires BOTH:
 *   1. `useModuleEnabled("AdtNotifications")` — the active persona's
 *      server-driven module grant.
 *   2. `useTopNavWorklistVisible()` — the Priority Worklist tab is ALSO
 *      visible. This conjunct is MAJOR 3's actual fix: without it, a
 *      notification click dead-ends on Member Search whenever the worklist
 *      itself is unavailable (`featureFlags.patientPriorityIndex=false`, or
 *      no `PriorityWorklist` persona grant) — the bell hands every
 *      notification off to `/worklist`'s discharge-focus signal, and the
 *      worklist page is the ONLY place the PP-login + NPI setup UI
 *      (`useWorklistAccessGate`) exists, so without it the bell's own
 *      identity gate (`resolveAdtBellProps`'s `shouldRender` in
 *      `adtBellLogic.ts`) can never resolve `ready` either — it could never
 *      populate at all.
 *
 * Deliberately lives in its OWN module, separate from `AdtBell.tsx`, and
 * imports nothing from `@astrana/ui`. `AppLayout` needs to answer "should the
 * bell show?" WITHOUT pulling the bell — and therefore `@astrana/ui/modules`
 * — into the app header's static import graph. Two reasons that matters:
 *
 *  1. Most personas won't have the `AdtNotifications` grant, so most
 *     sessions should never download the notifications module at all;
 *     `AppLayout` lazy-imports `AdtBell` only once this returns true.
 *  2. `@astrana/ui/modules`'s bundled Combobox emits an extensionless import
 *     of a nested `lucide-react/dynamic`. Vite's build resolves that; Vitest's
 *     stricter ESM resolver does not, so statically reaching the modules
 *     bundle from `AppLayout` stops `AppHeaderComposition.test.tsx` from even
 *     loading. Keeping it out of the static graph fixes that as a side effect
 *     — but the packaging bug itself is tracked separately upstream.
 */
export function useAdtBellVisible(): boolean {
  const worklistVisible = useTopNavWorklistVisible();
  const moduleEnabled = useModuleEnabled("AdtNotifications");
  return worklistVisible && moduleEnabled;
}
